import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { DatabaseBackup, Trash2, CheckCircle2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const errMsg = (e) => e.response?.data?.detail || 'Errore';

const saveBlob = (blob, name) => {
  const url = URL.createObjectURL(blob);
  const a = Object.assign(document.createElement('a'), { href: url, download: name });
  document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
};

export const CleanupDone = ({ run }) => (
  <Card className="border border-emerald-200 bg-emerald-50/60 rounded-xl" data-testid="cleanup-executed"><CardContent className="p-4 flex gap-2">
    <CheckCircle2 className="w-5 h-5 text-emerald-700 shrink-0" strokeWidth={1.5} />
    <p className="text-sm font-sans text-emerald-900">Pulizia eseguita il {new Date(run.finished_at || run.started_at).toLocaleString('it-IT')} (backup {run.backup_id}). Non può essere ripetuta.</p>
  </CardContent></Card>
);

export const CleanupExecute = ({ phrase, blocked, onDone }) => {
  const [backup, setBackup] = useState(null);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const downloadBackup = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/cleanup-plan/backup`, {}, { withCredentials: true, responseType: 'blob' });
      const id = r.headers['x-backup-id'];
      saveBlob(r.data, `womio_backup_${id}.json.gz`);
      setBackup({ id, sha: r.headers['x-backup-sha256'] });
      toast.success('Backup scaricato: conservalo prima di procedere');
    } catch (e) { toast.error('Backup non riuscito'); } finally { setBusy(false); }
  };
  const execute = async () => {
    if (!window.confirm('Ultima conferma: i dati di prova verranno cancellati definitivamente. Procedere?')) return;
    setBusy(true);
    try {
      await axios.post(`${API}/admin/cleanup-plan/execute`, { backup_id: backup.id, confirm_phrase: text }, { withCredentials: true });
      toast.success('Pulizia completata');
      onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Card className="border border-red-200 rounded-xl" data-testid="cleanup-execute"><CardContent className="p-5 space-y-4">
      <p className="text-sm font-semibold font-sans">Esegui la pulizia (una sola volta)</p>
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="outline" className="rounded-full" onClick={downloadBackup} disabled={busy} data-testid="cleanup-backup-btn">
          <DatabaseBackup className="w-4 h-4 mr-2" strokeWidth={1.5} /> 1. Scarica backup completo</Button>
        {backup && <span className="text-xs font-mono text-muted-foreground" data-testid="cleanup-backup-id">{backup.id} · {backup.sha.slice(0, 12)}…</span>}
      </div>
      <div className="space-y-1">
        <p className="text-xs font-sans text-muted-foreground">2. Scrivi <b className="text-foreground">{phrase}</b> per confermare</p>
        <Input value={text} onChange={e => setText(e.target.value)} disabled={!backup} className="h-10 max-w-md" data-testid="cleanup-confirm-input" />
      </div>
      <Button onClick={execute} disabled={busy || !backup || text.trim() !== phrase || blocked} className="rounded-full bg-red-600 hover:bg-red-700 text-white" data-testid="cleanup-execute-btn">
        <Trash2 className="w-4 h-4 mr-2" strokeWidth={1.5} /> 3. Cancella i dati di prova</Button>
      <p className="text-xs font-sans text-muted-foreground">Ti verrà chiesto il codice dell'app di autenticazione. Il backup vale 30 minuti{blocked ? ' — prima decidi i dati non previsti' : ''}.</p>
    </CardContent></Card>
  );
};
